import { DeliveryClient } from '@kontent-ai/delivery-sdk';
import packageInfo from '../package.json';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { fetchKeys, getLegacyToken } from './Utilities/KeyLoader';

const sourceTrackingHeaderName = 'X-KC-SOURCE';
const isPreview = (): boolean => true;

type GlobalHeadersType = {
  header: string;
  value: string;
};

const createClient = (): DeliveryClient => {
  const keys = fetchKeys();
  if (!keys) {
    throw new Error('API keys could not be loaded.');
  }
  const { environmentId, previewKey }: Partial<any> = keys;

  return new DeliveryClient({
    environmentId: environmentId,
    previewApiKey: previewKey,
    defaultQueryConfig: {
      usePreviewMode: isPreview(),
    },
    globalHeaders: (_queryConfig: any): GlobalHeadersType[] => [
      {
        header: sourceTrackingHeaderName,
        value: `${packageInfo.name};${packageInfo.version}`,
      },
    ],
  });
};

// Create client only when legacy token is available
// For modern JWT-based authentication, this client should not be used
const createClientIfNeeded = (): DeliveryClient | null => {
  try {
    const legacyToken = getLegacyToken();
    // Only create client if we have a non-empty, non-whitespace legacy token
    if (legacyToken && legacyToken.trim()) {
      return createClient();
    }
    return null;
  } catch (error) {
    // Client creation failed - will be handled in the provider
    console.log('[Client] Legacy client creation failed:', error);
    return null;
  }
};

type ClientState = [DeliveryClient | null, () => void];

const ClientContext = createContext<ClientState | undefined>(undefined);

export const useClient = (): ClientState => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider: FC<PropsWithChildren> = ({
  children,
}: PropsWithChildren<unknown>) => {
  const [client, setClient] = useState<DeliveryClient | null>(
    createClientIfNeeded(),
  );

  const updateClient = useCallback(() => {
    setClient(createClientIfNeeded());
  }, []);

  return (
    <ClientContext.Provider
      value={useMemo(() => [client, updateClient], [client, updateClient])}
    >
      {children}
    </ClientContext.Provider>
  );
};

export { createClient };
